import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  carregarQuestionarioPublico,
  enviarOrcamento,
  mensagemDoEnvio,
  validarLink,
  type EstadoDoLink,
  type RespostaEnviada,
} from "../lib/orcamento";
import type { Pergunta } from "../lib/irproposta";
import { buscarCep } from "../lib/cep";
import {
  cepCompleto,
  cpfValido,
  emailValido,
  mascararCep,
  mascararCpf,
  mascararInteiro,
  mascararMoeda,
  mascararTelefone,
  mascararUf,
  telefoneValido,
} from "../lib/mascaras";
import Carregando from "../componentes/Carregando";
import { CAMPO, LogoCF, Recado } from "../componentes/ui";

// O formulário de orçamento de IRPF — a ÚNICA tela do hub que um estranho abre.
//
// ---------------------------------------------------------------------------
// Quem está do outro lado, e o que isso exige
// ---------------------------------------------------------------------------
// Não é aluno. É o cliente de um aluno, que recebeu um link por WhatsApp e não
// conhece a Contabilidade Facilitada. Três consequências de desenho:
//
//   1. **Sem Topo e sem Rodapé do hub.** Menu de aluno, "Meu Perfil" e link para a
//      Faculdade não fazem sentido para quem não tem conta — e convidam ao clique
//      que leva a uma tela de login que ele não vai passar;
//   2. **O nome de quem vai receber aparece no alto.** Ele está entregando CPF e
//      endereço; saber para quem é a diferença entre preencher e fechar a aba;
//   3. **Nenhuma mensagem de erro nossa é técnica.** Ele não tem suporte para
//      acionar. Ou a frase diz o que fazer ("peça um link novo ao seu contador"),
//      ou ele desiste — e o lead que desiste é a razão de a ferramenta existir.
//
// ---------------------------------------------------------------------------
// Os enunciados e as MÁSCARAS não estão neste arquivo
// ---------------------------------------------------------------------------
// Vêm de `irp_questionario()`: o enunciado com o ano da temporada já interpolado, e
// `valor_formato` dizendo se o campo de valor é dinheiro ou contagem. Antes da
// 000069 essa segunda decisão era `q.codigo === "p1"` aqui dentro — regra de
// catálogo escrita em JavaScript, o mesmo defeito que a 000065 tirou dos enunciados.
//
// ---------------------------------------------------------------------------
// Por que role="group" e não <fieldset><legend>
// ---------------------------------------------------------------------------
// Porque `<legend>` é posicionado SOBRE a borda do fieldset por especificação, e com
// o card arredondado do hub o enunciado saía flutuando fora da caixa — foi o que o
// Victor viu na tela em 19/08. As saídas conhecidas (`float: left`, `display:
// contents`) trocam um bug de layout por outro em algum navegador. `role="group"` com
// `aria-labelledby` dá ao leitor de tela exatamente a mesma associação — o grupo tem
// nome acessível — e o título é um `<h3>` comum, que se comporta como qualquer outro.

export default function Orcamento() {
  const { token = "" } = useParams();
  const [link, setLink] = useState<EstadoDoLink | null>(null);
  const [questionario, setQuestionario] = useState<Pergunta[]>([]);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const [estado, perguntas] = await Promise.all([
        validarLink(token),
        carregarQuestionarioPublico(),
      ]);
      if (!ativo) return;
      setLink(estado);
      setQuestionario(perguntas);
    })();
    return () => {
      ativo = false;
    };
  }, [token]);

  if (!link) return <Carregando />;

  if (!link.valido) {
    return (
      <Moldura>
        <div className="surface p-7 text-center">
          <h1 className="font-display text-xl font-bold text-titulo">Formulário indisponível</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">
            {mensagemDoEnvio(link.motivo)}
          </p>
        </div>
      </Moldura>
    );
  }

  if (enviado) {
    return (
      <Moldura contador={link.contador}>
        <div className="surface p-7 text-center">
          <h1 className="font-display text-xl font-bold text-titulo">Recebemos as suas respostas</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">
            {link.contador
              ? `${link.contador} já pode ver o que você preencheu e vai entrar em contato com a proposta de honorários.`
              : "O seu contador já pode ver o que você preencheu e vai entrar em contato com a proposta de honorários."}
          </p>
          <p className="mx-auto mt-3 max-w-md text-sm text-cinza">Você pode fechar esta página.</p>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura contador={link.contador}>
      <Formulario
        token={token}
        anoCalendario={link.anoCalendario}
        questionario={questionario}
        aoEnviar={() => setEnviado(true)}
      />
    </Moldura>
  );
}

/**
 * A moldura da página pública.
 *
 * Fundo claro fixo, sem alternância de tema: quem abre isto não escolheu tema
 * nenhum no hub, e a marca da CF em azul sobre branco é o que ele vai reconhecer se
 * já viu o site.
 */
function Moldura({ contador, children }: { contador?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-fundo">
      <header className="bg-azul">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <LogoCF className="h-7" />
          {contador && (
            <p className="text-sm text-navy-mute">
              Orçamento para <span className="font-semibold text-white">{contador}</span>
            </p>
          )}
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">{children}</main>
      <footer className="bg-azul py-5 text-center">
        {/* "pelo", e não "pela": a marca é tratada no masculino em toda a casa — o
            rodapé do hub já diz "O aplicativo do Contabilidade Facilitada". */}
        <p className="text-xs text-navy-mute">
          Formulário fornecido pelo Contabilidade Facilitada
        </p>
      </footer>
    </div>
  );
}

type Contato = {
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  cep: string;
  endereco: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
};

/** O rótulo curto do bloco de observações; o enunciado longo vem do catálogo. */
const TITULO_OBS = "Outras informações";

const CONTATO_VAZIO: Contato = {
  nome: "", email: "", telefone: "", cpf: "",
  cep: "", endereco: "", complemento: "", bairro: "", cidade: "", estado: "",
};

function Formulario({
  token,
  anoCalendario,
  questionario,
  aoEnviar,
}: {
  token: string;
  anoCalendario: number;
  questionario: Pergunta[];
  aoEnviar: () => void;
}) {
  const [contato, setContato] = useState<Contato>(CONTATO_VAZIO);
  const [respostas, setRespostas] = useState<Record<string, RespostaEnviada>>({});
  const [observacoes, setObservacoes] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** Campos que o cliente já visitou — o erro só aparece depois que ele sai. */
  const [tocados, setTocados] = useState<Record<string, boolean>>({});
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepNaoAchado, setCepNaoAchado] = useState(false);

  /**
   * Todas as perguntas, na ordem do catálogo — a `obs` INCLUÍDA.
   *
   * Ela é desenhada como bloco de texto no lugar dela, e não fixa no fim: no Bubble
   * a 13 vem antes da 14, e tirá-la da lista foi o que inverteu as duas.
   */
  const perguntas = questionario;

  /**
   * O número que cada pergunta mostra — calculado UMA vez, sobre o questionário
   * inteiro.
   *
   * ANTES DE SIMPLIFICAR ISTO, LEIA: a primeira versão contava com o índice do
   * `.map()`, que roda DEPOIS do `.filter()` dos condicionais. Assim que o cliente
   * marcava "Sim" na pergunta 8, as filhas 8.1 e 8.2 entravam na lista e empurravam
   * todos os índices seguintes — a tela passava a mostrar "8. Possui bens", "8.
   * Possui dívidas", "9. Possui comprovantes". Numeração repetida num formulário de
   * imposto de renda faz o cliente achar que respondeu duas vezes a mesma coisa, e
   * faz o aluno ler a resposta errada quando ele confere pelo número.
   *
   * O número vem da posição entre as perguntas SEM PAI no catálogo completo — o que
   * o cliente vê não depende do que ele respondeu, e a `obs` fica com o 13 e a
   * origem com o 14, como no Bubble.
   */
  const numeros = useMemo(() => {
    const mapa = new Map<string, number>();
    let n = 0;
    for (const q of questionario) {
      if (q.pai) continue;
      mapa.set(q.codigo, ++n);
    }
    return mapa;
  }, [questionario]);

  function mudar(campo: keyof Contato, valor: string) {
    setContato((atual) => ({ ...atual, [campo]: valor }));
  }

  function responder(codigo: string, mudanca: RespostaEnviada) {
    setRespostas((atual) => ({ ...atual, [codigo]: { ...atual[codigo], ...mudanca } }));
  }

  /**
   * O CEP busca o endereço quando fica completo — e nunca sobrescreve o que a
   * pessoa já digitou.
   *
   * Prédio novo, loteamento e correção de logradouro acontecem; o ViaCEP não é a
   * autoridade sobre onde ela mora, ela é. E se a busca falhar (serviço fora,
   * internet ruim, CEP que não existe) nada acontece além de um aviso discreto: o
   * endereço todo é opcional.
   */
  async function completarPeloCep(cepDigitado: string) {
    if (!cepCompleto(cepDigitado)) return;
    setBuscandoCep(true);
    setCepNaoAchado(false);
    const achado = await buscarCep(cepDigitado);
    setBuscandoCep(false);
    if (!achado) {
      setCepNaoAchado(true);
      return;
    }
    setContato((atual) => ({
      ...atual,
      endereco: atual.endereco.trim() || achado.endereco,
      bairro: atual.bairro.trim() || achado.bairro,
      cidade: atual.cidade.trim() || achado.cidade,
      estado: atual.estado.trim() || achado.estado,
    }));
  }

  // Os erros de formato. Só aparecem depois que o campo foi tocado, para não
  // acusar quem ainda está digitando o primeiro dígito.
  const erroCpf =
    tocados.cpf && contato.cpf.trim() !== "" && !cpfValido(contato.cpf)
      ? "Confira o CPF — os números não fecham."
      : null;
  const erroEmail =
    tocados.email && contato.email.trim() !== "" && !emailValido(contato.email)
      ? "Confira o e-mail — algo como nome@dominio.com."
      : null;
  const erroTelefone =
    tocados.telefone && contato.telefone.trim() !== "" && !telefoneValido(contato.telefone)
      ? "Confira o telefone — com DDD, 10 ou 11 dígitos."
      : null;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    // Tudo tocado no submit: se algum campo tem erro, ele precisa aparecer.
    setTocados({ cpf: true, email: true, telefone: true });

    if (!contato.email.trim() && !contato.telefone.trim()) {
      return setErro(mensagemDoEnvio("contato-obrigatorio"));
    }
    if (contato.cpf.trim() !== "" && !cpfValido(contato.cpf)) {
      return setErro("O CPF informado não é válido. Confira os números ou deixe o campo em branco.");
    }
    if (contato.email.trim() !== "" && !emailValido(contato.email)) {
      return setErro("O e-mail informado não parece válido. Confira o endereço.");
    }
    if (contato.telefone.trim() !== "" && !telefoneValido(contato.telefone)) {
      return setErro("O telefone informado não parece válido. Use DDD e o número completo.");
    }

    setEnviando(true);
    const r = await enviarOrcamento(token, { ...contato, observacoes, respostas });
    setEnviando(false);
    if (!r.ok) return setErro(r.mensagem);
    aoEnviar();
  }

  return (
    <form onSubmit={enviar} className="space-y-5" noValidate>
      <div>
        <h1 className="font-display text-2xl font-bold text-titulo">
          Orçamento do Imposto de Renda
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-cinza">
          Responda sobre a sua situação no ano de {anoCalendario} para o seu contador montar a
          proposta de honorários. Leva poucos minutos.
        </p>
      </div>

      <Bloco titulo="Seus dados de contato">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Nome completo" obrigatorio>
            <input
              value={contato.nome}
              onChange={(e) => mudar("nome", e.target.value)}
              required
              minLength={2}
              autoComplete="name"
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="CPF" erro={erroCpf}>
            <input
              value={contato.cpf}
              onChange={(e) => mudar("cpf", mascararCpf(e.target.value))}
              onBlur={() => setTocados((t) => ({ ...t, cpf: true }))}
              inputMode="numeric"
              placeholder="000.000.000-00"
              aria-invalid={erroCpf ? true : undefined}
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="E-mail" erro={erroEmail}>
            <input
              type="email"
              value={contato.email}
              onChange={(e) => mudar("email", e.target.value.trim())}
              onBlur={() => setTocados((t) => ({ ...t, email: true }))}
              autoComplete="email"
              placeholder="nome@dominio.com"
              aria-invalid={erroEmail ? true : undefined}
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Telefone / WhatsApp" erro={erroTelefone}>
            <input
              value={contato.telefone}
              onChange={(e) => mudar("telefone", mascararTelefone(e.target.value))}
              onBlur={() => setTocados((t) => ({ ...t, telefone: true }))}
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 90000-0000"
              aria-invalid={erroTelefone ? true : undefined}
              className={CAMPO}
            />
          </Campo>
        </div>
        <p className="mt-2 text-sm text-cinza">
          Preencha ao menos o e-mail ou o telefone — é por onde o contador vai responder.
        </p>
      </Bloco>

      <Bloco
        titulo="Seu endereço"
        descricao="Usado no contrato de prestação de serviços, se você fechar com o contador. Pode deixar em branco agora."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo
            rotulo="CEP"
            dica={
              buscandoCep
                ? "Buscando o endereço…"
                : cepNaoAchado
                  ? "Não encontramos este CEP. Preencha o endereço à mão."
                  : "Digite o CEP e o resto do endereço aparece."
            }
          >
            <input
              value={contato.cep}
              onChange={(e) => {
                const novo = mascararCep(e.target.value);
                mudar("cep", novo);
                setCepNaoAchado(false);
                // Busca ao completar os oito dígitos, sem esperar o blur: quem
                // digita CEP no celular costuma ir direto para o campo seguinte.
                if (cepCompleto(novo)) void completarPeloCep(novo);
              }}
              onBlur={() => void completarPeloCep(contato.cep)}
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="00000-000"
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Endereço (rua e número)">
            <input
              value={contato.endereco}
              onChange={(e) => mudar("endereco", e.target.value)}
              autoComplete="street-address"
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Complemento">
            <input
              value={contato.complemento}
              onChange={(e) => mudar("complemento", e.target.value)}
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Bairro">
            <input
              value={contato.bairro}
              onChange={(e) => mudar("bairro", e.target.value)}
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Cidade">
            <input
              value={contato.cidade}
              onChange={(e) => mudar("cidade", e.target.value)}
              className={CAMPO}
            />
          </Campo>
          <Campo rotulo="Estado (UF)">
            <input
              value={contato.estado}
              onChange={(e) => mudar("estado", mascararUf(e.target.value))}
              maxLength={2}
              placeholder="SP"
              className={CAMPO}
            />
          </Campo>
        </div>
      </Bloco>

      {/* Renderiza na ORDEM DO CATÁLOGO, incluindo a `obs` no lugar dela.
          A primeira versão tirava a `obs` da lista e a colocava fixa no fim, e o
          resultado foi "14. Como conheceu" acima de "13. Outras informações" — no
          Bubble a 13 vem antes da 14. Deixar a ordem vir de `irp_perguntas.ordem`
          significa que reordenar o formulário é um `update`, não um deploy. */}
      {perguntas
        // Condicional só aparece quando a pergunta-mãe foi "Sim". Era assim no
        // Bubble, e é o que mantém o formulário curto para quem não é autônomo.
        .filter((q) => !q.pai || respostas[q.pai]?.sim === true)
        .map((q) =>
          q.tipo === "texto_longo" ? (
            <Bloco key={q.codigo} titulo={`${numeros.get(q.codigo) ?? ""}. ${TITULO_OBS}`}>
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-titulo">{q.rotulo}</span>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  className="w-full rounded-xl border-[1.5px] border-borda bg-superficie p-4 text-tinta transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
                  placeholder="Digite aqui…"
                />
              </label>
            </Bloco>
          ) : (
            <PerguntaDoFormulario
              key={q.codigo}
              pergunta={q}
              numero={numeros.get(q.codigo) ?? null}
              resposta={respostas[q.codigo]}
              aoResponder={(mudanca) => responder(q.codigo, mudanca)}
            />
          ),
        )}

      <div className="surface p-6">
        <p className="text-sm text-cinza">Revise as informações antes de enviar.</p>
        <button type="submit" disabled={enviando} className="pill-cta mt-3 disabled:opacity-60">
          {enviando ? "Enviando…" : "Enviar respostas"}
        </button>
        {erro && <Recado tipo="erro" texto={erro} />}
      </div>
    </form>
  );
}


function PerguntaDoFormulario({
  pergunta,
  numero,
  resposta,
  aoResponder,
}: {
  pergunta: Pergunta;
  numero: number | null;
  resposta: RespostaEnviada | undefined;
  aoResponder: (mudanca: RespostaEnviada) => void;
}) {
  const idTitulo = useId();
  const nome = `p_${pergunta.codigo}`;

  // "Sim / Não" e "Sim / Não / Não se aplica" são o mesmo desenho com listas
  // diferentes; a terceira opção existe só na pergunta do carnê-leão, e existia no
  // Bubble como um `text` em vez de boolean.
  const opcoes =
    pergunta.tipo === "escolha"
      ? (pergunta.opcoes ?? [])
      : pergunta.tipo === "sim_nao_na"
        ? ["Sim", "Não", "Não se aplica"]
        : ["Sim", "Não"];

  const eBooleano = pergunta.tipo === "sim_nao";
  const marcada = eBooleano
    ? resposta?.sim === true
      ? "Sim"
      : resposta?.sim === false
        ? "Não"
        : ""
    : (resposta?.escolha ?? "");

  // Quem diz qual máscara usar é o CATÁLOGO (`valor_formato`, 000069), não o slug.
  const eMoeda = pergunta.valor_formato === "moeda";

  return (
    // `role="group"` + `aria-labelledby`, e não `<fieldset><legend>`: o legend é
    // posicionado sobre a borda por especificação, e saía flutuando fora do card
    // arredondado. Ver o cabeçalho deste arquivo.
    <section
      role="group"
      aria-labelledby={idTitulo}
      className={`surface p-5 sm:p-6 ${pergunta.pai ? "sm:ml-8" : ""}`}
    >
      <h3 id={idTitulo} className="text-base font-semibold text-titulo">
        {numero ? `${numero}. ` : ""}
        {pergunta.rotulo}
      </h3>

      <div className="mt-3 space-y-1.5">
        {opcoes.map((o) => (
          <label key={o} className="flex min-h-11 cursor-pointer items-center gap-2.5">
            <input
              type="radio"
              name={nome}
              value={o}
              checked={marcada === o}
              onChange={() =>
                eBooleano ? aoResponder({ sim: o === "Sim" }) : aoResponder({ escolha: o })
              }
              className="size-4 accent-laranja"
            />
            <span className="text-sm text-tinta">{o}</span>
          </label>
        ))}
      </div>

      {/* O campo de valor só aparece no "Sim" — é o que o formulário do Bubble faz,
          e o que mantém a tela curta para quem responde "Não" em tudo. */}
      {pergunta.valor_tipo && resposta?.sim === true && (
        <label className="mt-3 block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            {pergunta.valor_rotulo}
          </span>
          <input
            value={resposta?.valor ?? ""}
            onChange={(e) => {
              const bruto = e.target.value;
              if (eMoeda) {
                // O campo MOSTRA "R$ 1.234,56" e manda "1234.56": ponto de milhar
                // numa coluna `numeric` é o mesmo defeito por outro caminho.
                const moeda = mascararMoeda(bruto);
                aoResponder({
                  valor: moeda.texto,
                  valorLimpo: moeda.numero === null ? "" : String(moeda.numero),
                });
              } else if (pergunta.valor_formato === "inteiro") {
                const inteiro = mascararInteiro(bruto);
                aoResponder({ valor: inteiro, valorLimpo: inteiro });
              } else {
                aoResponder({ valor: bruto, valorLimpo: bruto });
              }
            }}
            inputMode={pergunta.valor_tipo === "numero" ? "numeric" : undefined}
            placeholder={eMoeda ? "R$ 0,00" : (pergunta.valor_rotulo ?? "")}
            className={CAMPO}
          />
          {eMoeda && (
            <span className="mt-1 block text-sm text-cinza">
              Valor aproximado. Se não souber, deixe em branco.
            </span>
          )}
        </label>
      )}
    </section>
  );
}

function Bloco({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="surface p-5 sm:p-6">
      <h2 className="text-base font-semibold text-titulo">{titulo}</h2>
      {descricao && <p className="mt-1 text-sm leading-relaxed text-cinza">{descricao}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Campo({
  rotulo,
  obrigatorio,
  erro,
  dica,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  erro?: string | null;
  dica?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-titulo">
        {rotulo}
        {obrigatorio && <span className="text-laranja-texto"> *</span>}
      </span>
      {children}
      {/* Erro tem precedência sobre a dica: os dois juntos empurram o layout e a
          pessoa lê o menos importante primeiro. */}
      {erro ? (
        <span role="alert" className="mt-1 block text-sm font-medium text-erro-tinta">
          {erro}
        </span>
      ) : dica ? (
        <span className="mt-1 block text-sm text-cinza">{dica}</span>
      ) : null}
    </label>
  );
}
